/**
 * Post-op auto-send — what stands between the office and the master switch, and what the save bar says. Pure.
 * Plan: docs/plans/2026-10-15-post-op-auto-send-plan.md §1 (master switch states, sticky save bar), §5a.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE, with NAMED exports only — cypress/node-tests/post-op-editor.test.js executes it.
 *
 * THE SERVER IS THE GATE; this is the hint. forms-service lib/postOp/settings.js `enableBlockReason` refuses an OFF→ON save
 * with the same three reasons in the same order, and these are its sentences word for word (ENABLE_BLOCK_MESSAGES).
 * When a save comes back 409 ENABLE_BLOCKED the editor shows the SERVER's message, not this copy.
 */

/** Why a switch-on is refused, in the order the editor shows them — one reason at a time. */
export const BLOCK_REASONS = Object.freeze(['no_post_op_rows', 'sms_not_ready', 'email_not_ready']);

export const BLOCK_MESSAGES = Object.freeze({
  no_post_op_rows: 'Pick a post-op form on at least one row before turning on auto-send.',
  sms_not_ready: 'Text needs a texting number. Untick Text on every row, or contact Teamio to set one up.',
  email_not_ready: 'Email needs a verified sender. Untick Email on every row, or set one up in Settings → Email.',
});

/**
 * @param {{ sending:number, smsRows:number, emailRows:number }} analysis  analyzeRows() of the WORKING rows
 * @param {{ smsReady:boolean, emailReady:boolean }|null} readiness  getPostOpContext (PUT /get-forms-reminder-settings) `readiness`
 * @returns {{ reason:string, message:string }|null}  the FIRST blocking reason, or null when auto-send may be turned on
 */
export function blockedReason(analysis, readiness) {
  const a = analysis || {};
  const r = readiness || null;
  if (!r) return null; // not loaded yet — the editor does not render the switch before the context arrives
  let reason = null;
  if (!(a.sending > 0)) reason = 'no_post_op_rows';
  else if (a.smsRows > 0 && !r.smsReady) reason = 'sms_not_ready';
  else if (a.emailRows > 0 && !r.emailReady) reason = 'email_not_ready';
  return reason ? { reason, message: BLOCK_MESSAGES[reason] } : null;
}

/**
 * The master switch card.
 *   off      "Off — no post-op messages are being sent."
 *   on       "On since <server liveSince>. Only procedures completed after this time are sent."
 *   blocked  the one reason; the switch is locked — EXCEPT while it is ON, when it can always be switched OFF
 * `liveSinceText` is the already-formatted server `liveSince` ('' when the server has not stamped one).
 * @returns {{ kind:'off'|'on'|'blocked', checked:boolean, canToggle:boolean, message:string }}
 */
export function switchState({ enabled, liveSinceText, block } = {}) {
  if (block) {
    return { kind: 'blocked', checked: !!enabled, canToggle: !!enabled, message: block.message };
  }
  if (enabled) {
    return {
      kind: 'on',
      checked: true,
      canToggle: true,
      message: liveSinceText
        ? `On since ${liveSinceText}. Only procedures completed after this time are sent.`
        : 'On. Only procedures completed after you turned it on are sent.',
    };
  }
  return { kind: 'off', checked: false, canToggle: true, message: 'Off — no post-op messages are being sent.' };
}

/**
 * The sticky save bar. One state at a time:
 *   saving   → "Saving…"                              (nothing is clickable)
 *   error    → the save error                         (the last Save failed; Save is still available)
 *   issues   → "Fix N issue(s) on Post-op message to save"   (unsaved AND the message has problems — Save is disabled)
 *              Forms Settings passes `issueTab` (the first failing tab's name, e.g. 'New Patients'); absent ⇒ 'Post-op message', today's text.
 *   dirty    → "Unsaved changes"
 *   clean    → "All changes saved"
 */
export function saveBarState({ dirty, saving, saveError, issueCount, issueTab } = {}) {
  const issues = Number.isInteger(issueCount) && issueCount > 0 ? issueCount : 0;
  const where = typeof issueTab === 'string' && issueTab.trim() !== '' ? issueTab.trim() : 'Post-op message';
  if (saving) return { kind: 'saving', message: 'Saving…', canSave: false, canReset: false };
  if (saveError) return { kind: 'error', message: saveError, canSave: !!dirty && issues === 0, canReset: !!dirty };
  if (dirty && issues > 0) {
    return { kind: 'issues', message: `Fix ${issues} ${issues === 1 ? 'issue' : 'issues'} on ${where} to save`, canSave: false, canReset: true };
  }
  if (dirty) return { kind: 'dirty', message: 'Unsaved changes', canSave: true, canReset: true };
  return { kind: 'clean', message: 'All changes saved', canSave: false, canReset: false };
}
